import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Search, FileText, HelpCircle, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

/**
 * Reusable "Ask a Legal Question" search bar with live autocomplete across
 * judgment headline questions, legal provisions/sections and case titles.
 *
 * - Uncontrolled (e.g. Homepage Hero): just drop it in — typing + Enter
 *   navigates to /updates?q=...
 * - Controlled (e.g. Updates page): pass `value` + `onChange` to sync with
 *   the page's own live-filter state; set `autoNavigate={false}` so Enter
 *   doesn't leave the page (the grid below already filters live).
 */
export function LegalQuestionSearch({
  value,
  onChange,
  placeholder = "Search your legal question, section, or case name...",
  className = "",
  testId = "legal-question-search",
  autoNavigate = true,
}) {
  const navigate = useNavigate();
  const isControlled = value !== undefined;
  const [internal, setInternal] = useState("");
  const q = isControlled ? value : internal;
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);
  const debounceRef = useRef(null);

  const setQ = (v) => {
    if (isControlled) onChange?.(v);
    else setInternal(v);
  };

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!q || q.trim().length < 2) {
      setSuggestions([]);
      return undefined;
    }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/public/judgments?q=${encodeURIComponent(q.trim())}&limit=6`);
        setSuggestions(data?.items || []);
      } catch {
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [q]);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const goToResults = () => {
    setOpen(false);
    if (autoNavigate && q.trim()) navigate(`/updates?q=${encodeURIComponent(q.trim())}`);
  };

  const selectSuggestion = (j) => {
    setOpen(false);
    navigate(`/judgments/${j.slug || j.id}`);
  };

  return (
    <div ref={boxRef} className={cn("relative", className)}>
      <div className="relative">
        <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === "Enter") goToResults();
          }}
          placeholder={placeholder}
          className="h-12 rounded-xl pl-10 pr-4 text-sm shadow-sm"
          data-testid={testId}
          aria-label="Ask a legal question"
        />
        {loading && <Loader2 className="absolute right-3.5 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />}
      </div>
      {open && q.trim().length >= 2 && (suggestions.length > 0 || loading) && (
        <div
          className="absolute z-30 mt-1.5 w-full overflow-hidden rounded-xl border bg-popover shadow-lg"
          data-testid={`${testId}-suggestions`}
        >
          {loading && suggestions.length === 0 ? (
            <p className="px-4 py-3 text-sm text-muted-foreground">Searching…</p>
          ) : (
            <ul className="max-h-80 overflow-y-auto">
              {suggestions.map((j) => (
                <li key={j.id}>
                  <button
                    type="button"
                    onClick={() => selectSuggestion(j)}
                    className="flex w-full items-start gap-2 px-4 py-2.5 text-left text-sm transition-colors hover:bg-accent focus-visible:bg-accent focus-visible:outline-none"
                    data-testid={`${testId}-suggestion-${j.id}`}
                  >
                    <HelpCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
                    <span className="min-w-0">
                      <span className="block truncate font-medium">
                        {j.headline_question_en || j.plain_title_en || j.case_title}
                      </span>
                      <span className="block truncate text-xs text-muted-foreground">{j.case_title}</span>
                    </span>
                  </button>
                </li>
              ))}
              <li>
                <button
                  type="button"
                  onClick={goToResults}
                  className="flex w-full items-center gap-2 border-t px-4 py-2.5 text-left text-xs font-semibold text-primary transition-colors hover:bg-accent"
                  data-testid={`${testId}-see-all`}
                >
                  <FileText className="h-3.5 w-3.5" /> See all results for &ldquo;{q}&rdquo;
                </button>
              </li>
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

export default LegalQuestionSearch;
